import { DoubleSide, Group, Matrix3, Matrix4, Raycaster, Vector3, FrontSide } from "three";
import { modelPool } from "@modules/model";
import { CollisionShapeContainer } from "./CollisionShapeContainer";
import { Layer } from "../../rendering/RenderOrder";
/**
 * This UID indicates that a room wasn't properly initialized yet. This might happen if we try to add a collision shape
 * to a room that wasn't created yet.
 */
export const ROOM_UID_NOT_INITIALIZED = -1;
const tempMatrix = new Matrix4();
const tempWorldNormalMatrix = new Matrix3();
const tempLocalHitPoint = new Vector3();
const tempWorldHitPoint = new Vector3();
const tempLocalNormal = new Vector3();
const tempWorldNormal = new Vector3();
function getMaterials(shape) {
    return Array.isArray(shape.material) ? shape.material : [shape.material];
}
/**
 * A CollisionRoom holds all collision shapes for a specific room of the world. In order to perform ray casts or
 * intersection tests, a room needs to be activated first. A special room is the utility room which contains models
 * that should persist through room changes.
 */
export class CollisionRoom {
    constructor(uid = ROOM_UID_NOT_INITIALIZED) {
        this.uid = uid;
        this.name = this.findName(uid);
        this.isUtilityRoom = this.name === "_utility";
        this.active = false;
        this.collisionShapeContainer = new CollisionShapeContainer();
        this.activeCollisionShapeContainer = this.collisionShapeContainer;
        this.roomNode = new Group();
        this.roomNode.visible = false;
        this.rayCaster = new Raycaster();
        this.rayCaster.layers.enable(Layer.POSTPROCESSING_OVERLAY);
    }
    /// Getter and setter ///
    isActive() {
        return this.active;
    }
    isEmpty() {
        return this.collisionShapeContainer.isEmpty();
    }
    getAllCollisionShapes() {
        return this.collisionShapeContainer.getAllCollisionShapes();
    }
    /// Activation ///
    activate(mainScene, globalCollisionShapeContainer) {
        mainScene.add(this.roomNode);
        this.activeCollisionShapeContainer = globalCollisionShapeContainer;
        globalCollisionShapeContainer.attachContainer(this.collisionShapeContainer);
        this.active = true;
    }
    deactivate(mainScene, globalCollisionShapeContainer) {
        mainScene.remove(this.roomNode);
        this.activeCollisionShapeContainer = this.collisionShapeContainer;
        globalCollisionShapeContainer.detachContainer(this.collisionShapeContainer);
        this.active = false;
    }
    /// Collision shape creation and removal ///
    addCollisionShape(collisionShape) {
        this.collisionShapeContainer.addCollisionShape(collisionShape);
        this.roomNode.add(collisionShape);
    }
    updateCollisionShape(collisionShape) {
        this.collisionShapeContainer.updateCollisionShape(collisionShape);
    }
    removeCollisionShape(collisionShape) {
        this.collisionShapeContainer.removeCollisionShape(collisionShape);
        this.roomNode.remove(collisionShape);
        collisionShape.destroy();
    }
    /**
     * Detaches a collision shape from this room without destroying it - used when a shape is moving to a different
     * room (its owning model's room changed) rather than being removed entirely. Unlike removeCollisionShape, this
     * doesn't dispose the shape's geometry/bounds tree, since the caller is expected to immediately add it to another
     * room.
     */
    detachCollisionShape(collisionShape) {
        this.collisionShapeContainer.removeCollisionShape(collisionShape);
        this.roomNode.remove(collisionShape);
    }
    removeAllCollisionShapes() {
        this.collisionShapeContainer.clear();
        this.roomNode.clear();
    }
    /**
     * Whether a shape is currently present in the container actually used for selection/intersection tests in this
     * room (the room's own container, or the shared global one if this room is the active room). A shape can look
     * fine everywhere else (isSelectable true, valid geometry) while still being invisible to selection if it never
     * made it into this container - see CollisionWorld.updateOrCreateCollisionShape's room-reassignment handling.
     */
    isShapeActiveForSelection(uid) {
        return this.activeCollisionShapeContainer.getActiveCollisionShapes(true).some((shape) => shape.uid === uid);
    }
    // Collision and intersection ///
    performRaycast(origin, direction, targetCollisionShapeUIDs, backfaceCulling, includeTriggers) {
        var _a, _b;
        if (!this.active)
            return undefined;
        this.rayCaster.set(origin, direction);
        const selectableObjects = this.collectTargetCollisionShapes(targetCollisionShapeUIDs, includeTriggers !== null && includeTriggers !== void 0 ? includeTriggers : false);
        const previousSides = selectableObjects.map((shape) => getMaterials(shape).map((material) => material.side));
        this.setSides(selectableObjects, backfaceCulling);
        const intersections = this.rayCaster.intersectObjects(Array.from(selectableObjects));
        this.resetSides(selectableObjects, previousSides);
        if (intersections.length === 0)
            return undefined;
        const closestHit = intersections[0];
        const foundMesh = closestHit.object;
        const worldHitPoint = tempWorldHitPoint.copy(closestHit.point);
        const localHitPoint = tempLocalHitPoint.copy(worldHitPoint);
        foundMesh.worldToLocal(localHitPoint);
        const hitNormal = (_a = closestHit.normal) !== null && _a !== void 0 ? _a : (_b = closestHit.face) === null || _b === void 0 ? void 0 : _b.normal;
        const localNormal = tempLocalNormal.copy(hitNormal !== null && hitNormal !== void 0 ? hitNormal : new Vector3(0, 0, 0)).normalize();
        const worldNormal = tempWorldNormal
            .copy(localNormal)
            .applyMatrix3(tempWorldNormalMatrix.getNormalMatrix(foundMesh.matrixWorld))
            .normalize();
        return {
            uid: foundMesh.uid,
            distance: closestHit.distance,
            worldPosition: [worldHitPoint.x, worldHitPoint.y, worldHitPoint.z],
            localPosition: [localHitPoint.x, localHitPoint.y, localHitPoint.z],
            worldNormal: [worldNormal.x, worldNormal.y, worldNormal.z],
            localNormal: [localNormal.x, localNormal.y, localNormal.z],
        };
    }
    /** Share testedContactPairs only within one contact-update frame, never between independent hitpoint queries. */
    performIntersection(testCollisionShape, targetCollisionShapeUIDs, computeHitpoint, includeTriggers, testedContactPairs) {
        if (!this.active)
            return [];
        const targetCollisionShapes = this.collectTargetCollisionShapes(targetCollisionShapeUIDs, includeTriggers !== null && includeTriggers !== void 0 ? includeTriggers : false);
        return this.intersectTestShapeWithTargetShapes(testCollisionShape, targetCollisionShapes, computeHitpoint, testedContactPairs, targetCollisionShapeUIDs === undefined);
    }
    /// Debugging ///
    /**
     * Renders collision shapes as wireframe if set to true.
     */
    showCollisionShapes(show) {
        this.roomNode.visible = show;
    }
    /// Private intersection helper methods ///
    collectTargetCollisionShapes(targetCollisionShapeUIDs, includeTriggers) {
        if (targetCollisionShapeUIDs === undefined) {
            return this.activeCollisionShapeContainer.getActiveCollisionShapes(includeTriggers);
        }
        const targetCollisionShapes = [];
        // Use list of specified target collision shapes
        for (const targetModelUID of targetCollisionShapeUIDs) {
            const targetCollisionShape = this.activeCollisionShapeContainer.getCollisionShape(targetModelUID);
            if (targetCollisionShape === undefined)
                continue;
            targetCollisionShapes.push(targetCollisionShape);
        }
        return targetCollisionShapes;
    }
    intersectTestShapeWithTargetShapes(testCollisionShape, targetCollisionShapes, computeHitpoint = false, testedContactPairs, excludeSelf = false) {
        var _a, _b, _c;
        const detectedIntersections = [];
        const testMesh = testCollisionShape;
        const testBounds = testMesh.getWorldBounds();
        for (const targetCollisionShape of targetCollisionShapes) {
            if (excludeSelf && targetCollisionShape.uid === testMesh.uid)
                continue;
            const targetMesh = targetCollisionShape;
            const targetBoundsTree = targetMesh.geometry.boundsTree;
            if (targetBoundsTree === undefined)
                continue;
            const targetBounds = targetMesh.getWorldBounds();
            if (!testBounds.intersectsBox(targetBounds))
                continue;
            if (testedContactPairs !== undefined && !computeHitpoint) {
                const pair = testMesh.uid < targetMesh.uid ? `${testMesh.uid}:${targetMesh.uid}` : `${targetMesh.uid}:${testMesh.uid}`;
                if (testedContactPairs.has(pair))
                    continue;
                testedContactPairs.add(pair);
            }
            const transformMatrix = tempMatrix
                .copy(targetMesh.matrixWorld).invert()
                .multiply(testMesh.matrixWorld);
            // Check if an intersection exists
            let hasIntersection = (_a = targetBoundsTree === null || targetBoundsTree === void 0 ? void 0 : targetBoundsTree.intersectsGeometry(testMesh.geometry, transformMatrix)) !== null && _a !== void 0 ? _a : false;
            // Fallback to bounding box containment if no intersection was found because the bounds tree is not reliable for very small or very large objects
            if (!hasIntersection) {
                const containsBox = targetBounds.containsBox(testBounds) ||
                    testBounds.containsBox(targetBounds);
                if (containsBox)
                    hasIntersection = true;
            }
            if (!hasIntersection)
                continue;
            if (computeHitpoint) {
                const hit = (_b = targetBoundsTree === null || targetBoundsTree === void 0 ? void 0 : targetBoundsTree.closestPointToGeometry(testMesh.geometry, transformMatrix)) !== null && _b !== void 0 ? _b : undefined;
                if (hit !== undefined) {
                    const localHitPoint = (hit.point !== undefined) ?
                        [hit.point.x, hit.point.y, hit.point.z] :
                        [0, 0, 0];
                    let worldHitPoint = [0, 0, 0];
                    if (hit.point !== undefined) {
                        tempWorldHitPoint.copy(hit.point).applyMatrix4(targetMesh.matrixWorld);
                        worldHitPoint = [tempWorldHitPoint.x, tempWorldHitPoint.y, tempWorldHitPoint.z];
                    }
                    detectedIntersections.push({
                        uid: targetCollisionShape.uid,
                        distance: (_c = hit === null || hit === void 0 ? void 0 : hit.distance) !== null && _c !== void 0 ? _c : 0,
                        relativePosition: localHitPoint,
                        worldPosition: worldHitPoint
                    });
                }
            }
            else {
                detectedIntersections.push({
                    uid: targetCollisionShape.uid,
                    distance: 0,
                    relativePosition: [0, 0, 0],
                    worldPosition: [0, 0, 0]
                });
            }
        }
        return detectedIntersections;
    }
    /// Other private methods ///
    findName(uid) {
        var _a, _b;
        if (uid === ROOM_UID_NOT_INITIALIZED)
            return "Uninitialized Room";
        return (_b = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : "Unknown Room";
    }
    setSides(selectableObjects, backfaceCulling) {
        if (!backfaceCulling) {
            for (const selectableObject of selectableObjects) {
                for (const material of getMaterials(selectableObject)) {
                    material.side = DoubleSide;
                }
            }
        }
    }
    resetSides(selectableObjects, previousSides) {
        var _a, _b;
        for (let i = 0; i < selectableObjects.length; i++) {
            const materials = getMaterials(selectableObjects[i]);
            const previousMaterialSides = (_a = previousSides[i]) !== null && _a !== void 0 ? _a : [];
            for (let j = 0; j < materials.length; j++) {
                materials[j].side = (_b = previousMaterialSides[j]) !== null && _b !== void 0 ? _b : FrontSide;
            }
        }
    }
}
